import MoneyIcon from "@app/icons/dashboard/money";
import DashboardService from "@app/services/http/dashboard.service";
import { StatisticsState } from "@app/types";
import {
  PaymentInformationLabel,
  PaymentInformation as PaymentInformationType,
} from "@app/types/dashboard.type";
import clsx from "clsx";
import { memo, useEffect, useState } from "react";

const Processing = ({
  label,
  value,
  backgroundColor,
}: {
  label: string;
  value: number;
  backgroundColor: string;
}) => {
  return (
    <div className="w-full">
      <div
        className={clsx(
          "flex-1 flex items-center justify-center h-[40px] rounded-[8px]",
          backgroundColor
        )}
        style={{ width: `${value}%` }}
      >
        <p className="text-[12px] font-medium text-white">{label}</p>
      </div>
    </div>
  );
};

const Chart = ({
  data,
  getBgColorByLabel,
}: {
  data: PaymentInformationType | undefined;
  getBgColorByLabel: (label: PaymentInformationLabel) => string;
}) => {
  const maxMoney = Math.max(...(data?.items.map((item) => item.value) || []));

  return (
    <div className="relative">
      <div className="flex flex-col gap-[20px] mt-[10px]">
        {data?.items.map((item, index, array) => (
          <div key={index}>
            <div className="flex items-center gap-[20px]">
              <p className="text-[11px] text-[#A6A8B1]">
                {array.length - index}
              </p>
              <Processing
                label={item.label}
                value={Math.round((item.value / maxMoney) * 100)}
                backgroundColor={getBgColorByLabel(item.label)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PaymentInformation = ({ filter }: { filter: StatisticsState }) => {
  const [data, setData] = useState<PaymentInformationType>();

  useEffect(() => {
    DashboardService.getPaymentInformation({
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setData(res.data);
    });
  }, [filter.startDate, filter.endDate]);

  const getBgColorByLabel = (label: PaymentInformationLabel) => {
    switch (label) {
      case PaymentInformationLabel.APPLE_STORE:
        return "bg-[#71DD37]";
      case PaymentInformationLabel.CH_PLAY:
        return "bg-[#5570F1]";
      case PaymentInformationLabel.BANK:
        return "bg-[#FFAB00]";
      default:
        return "bg-[#FFAB00]";
    }
  };

  return (
    <div className="p-[24px] bg-white rounded-xl flex gap-[20px]">
      <div className="flex-1">
        <div className="flex gap-1 items-center">
          <MoneyIcon />
          <p className="text-[18px] text-[#45464E] font-medium">
            Số tiền được thanh toán qua các cổng
          </p>
        </div>
        <p className="text-[32px] text-[#1C1D22] font-medium">
          {data?.totalMoney ? data?.totalMoney.toLocaleString("de-DE") : 0}đ
        </p>

        <Chart data={data} getBgColorByLabel={getBgColorByLabel} />
      </div>
      <div className="flex flex-col self-stretch justify-center gap-[40px]">
        {!!data?.items.length &&
          data.items.map((item, index) => (
            <div key={index}>
              <div className="flex gap-2 items-center">
                <div
                  className={clsx(
                    "w-[8px] h-[8px] rounded-full bg-[#5570F1]",
                    getBgColorByLabel(item.label)
                  )}
                ></div>
                <p className="text-[#45464E] text-[11px]">{item.label}</p>
              </div>
              <p className="text-[#45464E] text-[11px]">
                {item.value.toLocaleString("de-DE")}đ
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};

export default memo(PaymentInformation);
